import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Bus,
  History,
  Pencil,
  ShieldAlert,
  UserRoundCog,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ROLES } from '../config/accessControl';
import { DRIVER_PERSON_TYPE_LABELS, DRIVER_STATUSES } from '../config/driverTypes';
import { VEHICLE_STATUS_META, VEHICLE_STATUSES } from '../config/vehicleStatus';
import { useAuth } from '../context/AuthContext';
import { vehicleService } from '../services';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('es-PY', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    : '—';
const statusOptions = Object.entries(VEHICLE_STATUS_META)
  .map(([value, meta]) => ({ value, label: meta.label }));

function DataItem({ label, value }) {
  return <div><dt className="text-xs font-medium uppercase tracking-wide text-content-muted">{label}</dt><dd className="mt-1 text-sm font-medium text-content-primary">{value || '—'}</dd></div>;
}

function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [editForm, setEditForm] = useState({});

  const canManage = vehicle && (
    user.role === ROLES.ADMINISTRADOR ||
    (user.role === ROLES.COORDINADOR && user.relaciones?.coordinador_id === vehicle.coordinator_id)
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await vehicleService.getDetailForActor(id, user);
      setVehicle(response.data);
    } catch (requestError) {
      setError(requestError.message);
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setEditForm({
      vehicle_type: vehicle.vehicle_type,
      brand: vehicle.brand,
      model: vehicle.model,
      year: String(vehicle.year),
      color: vehicle.color,
      license_plate: vehicle.license_plate,
      capacity: String(vehicle.capacity),
      zone: vehicle.zone,
      observations: vehicle.observations,
    });
    setError('');
    setModal('edit');
  };

  const openDriver = async () => {
    setError('');
    try {
      const response = await vehicleService.getFormOptions(user, vehicle.coordinator_id);
      setDrivers(response.data.drivers);
      setSelectedDriverId(vehicle.driver_id);
      setModal('driver');
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const runAction = async (action, successMessage) => {
    setSaving(true);
    setError('');
    try {
      await action();
      setModal(null);
      setConfirmDeactivate(false);
      setFeedback(successMessage);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = () => runAction(
    () => vehicleService.updateForActor(id, editForm, user),
    'Datos del móvil actualizados y registrados en el historial.'
  );
  const saveDriver = () => runAction(
    () => vehicleService.changeDriver(id, selectedDriverId, user),
    'Conductor cambiado. El anterior quedó liberado y la operación fue auditada.'
  );
  const saveStatus = () => runAction(
    () => vehicleService.changeStatus(id, selectedStatus, user),
    'Estado del móvil actualizado.'
  );
  const deactivate = () => runAction(
    () => vehicleService.changeStatus(id, VEHICLE_STATUSES.OUT_OF_SERVICE, user),
    'Móvil marcado como fuera de servicio.'
  );

  const availableDrivers = useMemo(() => drivers.filter((driver) =>
    driver.id === vehicle?.driver_id || driver.status !== DRIVER_STATUSES.ASSIGNED
  ), [drivers, vehicle?.driver_id]);

  if (loading) return <div className="grid min-h-96 place-items-center"><LoadingSpinner label="Cargando móvil" /></div>;
  if (!vehicle) return <EmptyState icon={Bus} title="No se pudo abrir el móvil" description={error || 'El móvil no existe o está fuera de tu estructura.'} actionLabel="Volver a móviles" onAction={() => navigate('/moviles')} />;

  const statusMeta = VEHICLE_STATUS_META[vehicle.status] || { label: vehicle.status, variant: 'neutral' };
  const occupancyPercentage = vehicle.capacity
    ? Math.round((vehicle.occupancy / vehicle.capacity) * 100)
    : 0;

  return (
    <div>
      <Link to="/moviles" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:underline"><ArrowLeft className="size-4" /> Volver a móviles</Link>
      <div className="mb-7 flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
        <div>
          <div className="mb-2 flex gap-2"><Badge variant={statusMeta.variant} dot>{statusMeta.label}</Badge><Badge variant="neutral">{vehicle.vehicle_type}</Badge></div>
          <h2 className="text-2xl font-semibold tracking-tight text-content-primary">{vehicle.brand} {vehicle.model}</h2>
          <p className="mt-1 font-mono text-sm font-semibold text-content-muted">{vehicle.license_plate}</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={openEdit}><Pencil className="size-4" /> Editar</Button>
            <Button variant="outline" size="sm" onClick={openDriver}><UserRoundCog className="size-4" /> Cambiar conductor</Button>
            <Button variant="outline" size="sm" onClick={() => { setSelectedStatus(vehicle.status); setModal('status'); }}><ShieldAlert className="size-4" /> Cambiar estado</Button>
            <Button variant="danger" size="sm" onClick={() => setConfirmDeactivate(true)} disabled={vehicle.status === VEHICLE_STATUSES.OUT_OF_SERVICE}>Desactivar</Button>
          </div>
        )}
      </div>

      {feedback && <Alert variant="success" title="Operación completada" className="mb-5">{feedback}</Alert>}
      {error && !modal && <Alert variant="danger" title="No se pudo completar" className="mb-5">{error}</Alert>}

      <div className="grid gap-5 xl:grid-cols-3">
        <Card title="Datos del vehículo" className="xl:col-span-2">
          <dl className="grid gap-5 sm:grid-cols-3">
            <DataItem label="Tipo" value={vehicle.vehicle_type} />
            <DataItem label="Marca y modelo" value={`${vehicle.brand} ${vehicle.model}`} />
            <DataItem label="Año y color" value={`${vehicle.year} · ${vehicle.color}`} />
            <DataItem label="Coordinador responsable" value={vehicle.coordinatorName} />
            <DataItem label="Zona" value={vehicle.zone} />
            <DataItem label="Estado" value={statusMeta.label} />
          </dl>
        </Card>
        <Card title="Conductor principal">
          <p className="font-semibold text-content-primary">{vehicle.driverName}</p>
          <p className="mt-1 text-sm text-content-muted">{DRIVER_PERSON_TYPE_LABELS[vehicle.driver?.person_type] || 'Tipo no disponible'}</p>
          <p className="mt-3 text-xs text-content-muted">Licencia {vehicle.driver?.license_number || 'sin datos'} · vence {vehicle.driver?.license_expiration || '—'}</p>
        </Card>
        <Card title="Ocupación">
          <div className="flex items-end justify-between"><p className="text-3xl font-semibold text-content-primary">{vehicle.occupancy}/{vehicle.capacity}</p><p className="text-sm font-medium text-primary-700">{occupancyPercentage}%</p></div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, occupancyPercentage)}%` }} /></div>
          <p className="mt-2 text-xs text-content-muted">{vehicle.availableCapacity} lugares disponibles</p>
        </Card>
        <Card title="Observaciones" className="xl:col-span-2"><p className="whitespace-pre-wrap text-sm text-content-secondary">{vehicle.observations || 'Sin observaciones.'}</p></Card>
      </div>

      <div className="mt-5 grid gap-5">
        <Card title="Historial de cambios">
          <div className="max-h-80 space-y-3 overflow-y-auto">
            {vehicle.history.map((item) => (
              <div key={item.id} className="flex gap-3">
                <span className="mt-1 grid size-7 shrink-0 place-items-center rounded-full bg-slate-100 text-content-muted"><History className="size-3.5" /></span>
                <div><p className="text-sm text-content-secondary">{item.detail}</p><p className="mt-1 text-xs text-content-muted">{formatDate(item.createdAt)} · {item.action}</p></div>
              </div>
            ))}
            {!vehicle.history.length && <p className="py-8 text-center text-sm text-content-muted">Sin cambios registrados.</p>}
          </div>
        </Card>
      </div>

      <Modal open={modal === 'edit'} onClose={() => !saving && setModal(null)} size="lg" title="Editar móvil" footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancelar</Button><Button onClick={saveEdit} loading={saving}>Guardar cambios</Button></>}>
        <div className="grid gap-4 sm:grid-cols-2">
          {error && <Alert variant="danger" title="No se pudo editar" className="sm:col-span-2">{error}</Alert>}
          <Input id="detail-vehicle-type" label="Tipo" value={editForm.vehicle_type || ''} onChange={(event) => setEditForm((current) => ({ ...current, vehicle_type: event.target.value }))} />
          <Input id="detail-vehicle-brand" label="Marca" value={editForm.brand || ''} onChange={(event) => setEditForm((current) => ({ ...current, brand: event.target.value }))} />
          <Input id="detail-vehicle-model" label="Modelo" value={editForm.model || ''} onChange={(event) => setEditForm((current) => ({ ...current, model: event.target.value }))} />
          <Input id="detail-vehicle-year" type="number" label="Año" value={editForm.year || ''} onChange={(event) => setEditForm((current) => ({ ...current, year: event.target.value }))} />
          <Input id="detail-vehicle-color" label="Color" value={editForm.color || ''} onChange={(event) => setEditForm((current) => ({ ...current, color: event.target.value }))} />
          <Input id="detail-vehicle-plate" label="Chapa" value={editForm.license_plate || ''} onChange={(event) => setEditForm((current) => ({ ...current, license_plate: event.target.value }))} />
          <Input id="detail-vehicle-capacity" type="number" min="1" label="Capacidad" value={editForm.capacity || ''} onChange={(event) => setEditForm((current) => ({ ...current, capacity: event.target.value }))} />
          <Input id="detail-vehicle-zone" label="Zona" value={editForm.zone || ''} onChange={(event) => setEditForm((current) => ({ ...current, zone: event.target.value }))} />
          <Textarea id="detail-vehicle-observations" label="Observaciones" className="sm:col-span-2" value={editForm.observations || ''} onChange={(event) => setEditForm((current) => ({ ...current, observations: event.target.value }))} />
        </div>
      </Modal>

      <Modal open={modal === 'driver'} onClose={() => !saving && setModal(null)} size="sm" title="Cambiar conductor" description="El conductor anterior quedará disponible." footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancelar</Button><Button onClick={saveDriver} loading={saving} disabled={!selectedDriverId || selectedDriverId === vehicle.driver_id}>Confirmar cambio</Button></>}>
        {error && <Alert variant="danger" title="No se pudo cambiar" className="mb-4">{error}</Alert>}
        <Select id="detail-driver" label="Nuevo conductor principal" value={selectedDriverId} onChange={(event) => setSelectedDriverId(event.target.value)} options={availableDrivers.map((driver) => ({ value: driver.id, label: driver.name }))} />
      </Modal>

      <Modal open={modal === 'status'} onClose={() => !saving && setModal(null)} size="sm" title="Cambiar estado" footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancelar</Button><Button onClick={saveStatus} loading={saving} disabled={selectedStatus === vehicle.status}>Guardar estado</Button></>}>
        {error && <Alert variant="danger" title="No se pudo cambiar" className="mb-4">{error}</Alert>}
        <Select id="detail-status" label="Estado operativo" value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)} options={statusOptions} />
      </Modal>

      <ConfirmDialog open={confirmDeactivate} onClose={() => !saving && setConfirmDeactivate(false)} onConfirm={deactivate} loading={saving} danger title="Desactivar móvil" description="El móvil quedará fuera de servicio y su asignación operativa se marcará como inactiva." confirmLabel="Desactivar" />
    </div>
  );
}

export default VehicleDetailPage;
